import moment from 'moment';

/**
 * ТЗ 0078 — аналитика договоров ERP. Словари и чистые помощники: периоды,
 * подписи ряда, «часы до подписи», CSV таблицы продавцов.
 *
 * Модуль без React, Apollo и i18n: подписи — ключи `esign.analytics.*`,
 * их переводит вызывающий.
 */

/** Быстрые периоды фильтра; `custom` — две даты руками. */
export const CONTRACT_ANALYTICS_PRESETS = [
  '30d',
  '90d',
  '12m',
  'custom',
] as const;

export type ContractAnalyticsPreset = typeof CONTRACT_ANALYTICS_PRESETS[number];

export const CONTRACT_ANALYTICS_DEFAULT_PRESET: ContractAnalyticsPreset = '90d';

/** Шаг ряда: бэкенд знает `week` (по умолчанию) и `month`. */
export const CONTRACT_ANALYTICS_GRANULARITIES = ['week', 'month'] as const;

export type ContractAnalyticsGranularity = typeof CONTRACT_ANALYTICS_GRANULARITIES[number];

/** Вид договора в фильтре: пусто — оба «продажных» вида. */
export const CONTRACT_ANALYTICS_KINDS = ['', 'student', 'company'] as const;

/** Ступени воронки — в порядке прохождения. */
export const CONTRACT_FUNNEL_STEPS = [
  'created',
  'sent',
  'opened',
  'signed',
  'enrolled',
] as const;

export type ContractFunnelStep = typeof CONTRACT_FUNNEL_STEPS[number];

const ISO = 'YYYY-MM-DD';

export interface IAnalyticsRange {
  from: string | null;
  to: string | null;
}

/**
 * Границы быстрого периода — ВКЛЮЧИТЕЛЬНО, по календарю: «30 дней» —
 * сегодня и 29 дней до него (бэкенд берёт `dateTo` целым днём). `custom`
 * здесь не считается — его даты задаёт человек.
 */
export const contractAnalyticsRange = (
  preset: Exclude<ContractAnalyticsPreset, 'custom'>,
  today: Date | string = new Date(),
): IAnalyticsRange => {
  const end = moment(today).startOf('day');
  let start = end.clone();
  if (preset === '30d') start = end.clone().subtract(29, 'days');
  else if (preset === '90d') start = end.clone().subtract(89, 'days');
  else start = end.clone().subtract(12, 'months').add(1, 'day');
  return { from: start.format(ISO), to: end.format(ISO) };
};

/**
 * Подпись точки ряда: неделя `2026-W37` → понедельник этой недели
 * («07.09»; не текущего года — «07.09.25», иначе за год две одинаковые
 * подписи), месяц `2026-09` → «09.2026». Незнакомый формат — как пришёл.
 */
export const analyticsPeriodLabel = (
  period: string,
  now: moment.Moment = moment(),
): string => {
  const week = /^(\d{4})-W(\d{2})$/.exec(period);
  if (week) {
    const monday = moment(`${week[1]}-W${week[2]}-1`, 'GGGG-[W]WW-E', true);
    if (!monday.isValid()) return period;
    return monday.format(monday.year() === now.year() ? 'DD.MM' : 'DD.MM.YY');
  }
  const month = /^(\d{4})-(\d{2})$/.exec(period);
  if (month) return `${month[2]}.${month[1]}`;
  return period;
};

/** Доля в процентах, целым; `null` — делить не на что. */
export const analyticsPct = (part: number, total: number): number | null =>
  total > 0 ? Math.round((part * 100) / total) : null;

export interface IHoursLabel {
  /**
   * Ключ перевода: `esign.analytics.underHour`, `esign.analytics.hours` или
   * `esign.analytics.days`.
   */
  key: string;
  n: string;
}

/**
 * «Время до подписи» человеческими единицами: меньше двух суток — часами,
 * дальше — днями (одна цифра после запятой). `null` — нет подписанных.
 */
export const analyticsHoursLabel = (
  hours: number | null | undefined,
): IHoursLabel | null => {
  if (hours === null || hours === undefined || !Number.isFinite(hours)) {
    return null;
  }
  // «0 ч» читалось бы как «мгновенно» или как пустое значение.
  if (hours < 1) return { key: 'esign.analytics.underHour', n: '1' };
  if (hours < 48) {
    return { key: 'esign.analytics.hours', n: String(Math.round(hours)) };
  }
  const days = Math.round((hours / 24) * 10) / 10;
  return { key: 'esign.analytics.days', n: String(days) };
};

/* ── CSV продавцов ────────────────────────────────────────────────────── */

export interface ISellerStatRow {
  sellerId?: string | null;
  name: string;
  created: number;
  sent: number;
  signed: number;
  signedInPeriod: number;
  conversionPct?: number | null;
  medianHours?: number | null;
  amounts?: ReadonlyArray<{ currency: string; amount: string }> | null;
}

export interface ISellerCsvHeaders {
  seller: string;
  sellerId: string;
  created: string;
  sent: string;
  signed: string;
  signedInPeriod: string;
  conversion: string;
  medianHours: string;
  /** Префикс колонки суммы: «Сумма» → «Сумма PLN», «Сумма EUR». */
  amount: string;
  /** Имя строки без продавца — как в таблице на экране. */
  noSeller: string;
}

const UTF8_BOM = '\uFEFF';
const CSV_SEP = ';';
const ROW_SEP = '\r\n';

/**
 * Поле CSV по RFC 4180, но с `;`: Excel с польской и русской локалью делит
 * колонки точкой с запятой, а запятая у него — десятичный знак.
 */
const csvField = (value: string): string =>
  /[";\r\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;

/** Число для Excel этой локали: десятичная запятая, пусто вместо `null`. */
const csvNumber = (value: number | string | null | undefined): string => {
  if (value === null || value === undefined || value === '') return '';
  const number = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(number)) return '';
  return String(value).replace('.', ',');
};

/** Валюты сумм по всем строкам — колонки CSV, по алфавиту. */
export const sellerCurrencies = (
  rows: ReadonlyArray<ISellerStatRow>,
): string[] =>
  Array.from(
    new Set(
      rows.flatMap((row) => (row.amounts ?? []).map((item) => item.currency)),
    ),
  ).sort();

/**
 * CSV таблицы продавцов: UTF-8 с BOM (Excel иначе читает кириллицу и
 * польские буквы как ANSI), разделитель `;`, строки через CRLF.
 *
 * Колонки сумм — по одной на валюту и только при `amountsVisible`: без
 * права на суммы бэкенд их не отдаёт, и пустые колонки читались бы как
 * «продал на ноль».
 */
export const buildSellersCsv = (
  rows: ReadonlyArray<ISellerStatRow>,
  headers: ISellerCsvHeaders,
  amountsVisible: boolean,
): string => {
  const currencies = amountsVisible ? sellerCurrencies(rows) : [];
  const head = [
    headers.seller,
    headers.sellerId,
    headers.created,
    headers.sent,
    headers.signed,
    headers.conversion,
    headers.signedInPeriod,
    headers.medianHours,
    ...currencies.map((currency) => `${headers.amount} ${currency}`),
  ];
  const lines = rows.map((row) => [
    row.sellerId ? row.name : headers.noSeller,
    row.sellerId ?? '',
    csvNumber(row.created),
    csvNumber(row.sent),
    csvNumber(row.signed),
    csvNumber(row.conversionPct),
    csvNumber(row.signedInPeriod),
    csvNumber(row.medianHours),
    ...currencies.map((currency) =>
      csvNumber(
        (row.amounts ?? []).find((item) => item.currency === currency)
          ?.amount ?? null,
      ),
    ),
  ]);
  return (
    UTF8_BOM +
    [head, ...lines]
      .map((line) => line.map((cell) => csvField(cell)).join(CSV_SEP))
      .join(ROW_SEP)
  );
};
